import React, { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, ArrowRight, Package, Truck, CheckCircle, Clock, XCircle, Download, Edit, Search, FileSpreadsheet, Trash2, Printer, Eye, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import IUTPrint from '@/components/IUTPrint';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_transit: 'bg-blue-100 text-blue-800 border-blue-200',
  arrived: 'bg-purple-100 text-purple-800 border-purple-200',
  received: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const statusIcons = {
  pending: <Clock className="h-3 w-3" />,
  in_transit: <Truck className="h-3 w-3" />,
  arrived: <MapPin className="h-3 w-3" />,
  received: <CheckCircle className="h-3 w-3" />,
  cancelled: <XCircle className="h-3 w-3" />
};

export default function InterUnitTransferPage() {
  const { user, getAuthHeader } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [printCopyType, setPrintCopyType] = useState('ORIGINAL');
  
  // Print ref
  const printRef = useRef();
  
  // User's store_incharge location IDs (for receive button visibility)
  const [userStoreLocations, setUserStoreLocations] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    fromUnitId: 'all',
    toUnitId: 'all',
    search: ''
  });
  
  // Multi-item receive data (array of items with qty_ok/qty_not_ok)
  const [receiveItems, setReceiveItems] = useState([]);
  
  // Issue form data - multi-item support
  const [formData, setFormData] = useState({
    from_unit_id: '',
    to_unit_id: '',
    remarks: ''
  });
  const [issueItems, setIssueItems] = useState([]);  // Array of {item_id, item_name, quantity, unit, remarks}
  
  // Edit form data - multi-item support
  const [editData, setEditData] = useState({
    from_unit_id: '',
    to_unit_id: '',
    remarks: ''
  });
  const [editItems, setEditItems] = useState([]);  // Array of {item_name, quantity, unit, item_code, remarks}
  
  // Check if user has inter_unit_transfer module access
  const hasIUTAccess = user?.allowed_modules?.includes('inter_unit_transfer') || user?.is_owner;

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: selectedTransfer ? `IUT-${selectedTransfer.transfer_number}` : 'IUT',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transfersRes, locationsRes, itemsRes] = await Promise.all([
        axios.get(`${API_URL}/inter-unit-transfers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/locations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() })
      ]);
      setTransfers(transfersRes.data);
      setLocations(locationsRes.data.filter(l => l.is_active !== false));
      setItems(itemsRes.data);
      
      // Fetch user's store_incharge mappings
      try {
        const mappingsRes = await axios.get(`${API_URL}/location-team-mappings/by-role/store_incharge`, { headers: getAuthHeader() });
        const userMappings = mappingsRes.data.filter(m => m.user_id === user?.id);
        setUserStoreLocations(userMappings.map(m => m.location_id));
      } catch (err) {
        console.log('Could not fetch team mappings:', err);
        setUserStoreLocations([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if current user can receive this transfer
  // Allowed: store_incharge of to_unit_id OR users with IUT module access
  const canReceive = (transfer) => {
    if (hasIUTAccess) return true;
    return userStoreLocations.includes(transfer.to_unit_id);
  };
  
  // Check if current user can cancel/edit this transfer
  // Allowed: creator of the IUT OR users with IUT module access
  const canCancel = (transfer) => {
    if (hasIUTAccess) return true;
    return transfer.created_by === user?.id;
  };
  
  // Same as canCancel - creator can edit pending IUTs
  const canEdit = (transfer) => {
    if (transfer.status !== 'pending') return false;
    if (hasIUTAccess) return true;
    return transfer.created_by === user?.id;
  };

  // Filter transfers based on filters and active tab
  const filteredTransfers = useMemo(() => {
    let result = transfers;
    
    // Apply tab filter
    if (activeTab === 'pending') {
      result = result.filter(t => t.status === 'pending');
    } else if (activeTab === 'in_transit') {
      result = result.filter(t => t.status === 'in_transit');
    } else if (activeTab === 'arrived') {
      result = result.filter(t => t.status === 'arrived');
    } else if (activeTab === 'received') {
      result = result.filter(t => t.status === 'received');
    } else if (activeTab === 'my_issued') {
      result = result.filter(t => t.created_by === user?.id);
    } else if (activeTab === 'my_to_receive') {
      // Show arrived IUTs pending store receiving at user's locations
      result = result.filter(t => 
        t.status === 'arrived' && userStoreLocations.includes(t.to_unit_id)
      );
    }
    
    // Apply date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter(t => new Date(t.created_at) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(t => new Date(t.created_at) <= toDate);
    }
    
    // Apply unit filters
    if (filters.fromUnitId && filters.fromUnitId !== 'all') {
      result = result.filter(t => t.from_unit_id === filters.fromUnitId);
    }
    if (filters.toUnitId && filters.toUnitId !== 'all') {
      result = result.filter(t => t.to_unit_id === filters.toUnitId);
    }
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(t => 
        t.transfer_number?.toLowerCase().includes(searchLower) ||
        t.item_name?.toLowerCase().includes(searchLower) ||
        t.from_unit_name?.toLowerCase().includes(searchLower) ||
        t.to_unit_name?.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [transfers, activeTab, filters, user?.id, userStoreLocations]);

  // Get counts for tabs
  const tabCounts = useMemo(() => ({
    all: transfers.length,
    pending: transfers.filter(t => t.status === 'pending').length,
    in_transit: transfers.filter(t => t.status === 'in_transit').length,
    arrived: transfers.filter(t => t.status === 'arrived').length,
    received: transfers.filter(t => t.status === 'received').length,
    my_issued: transfers.filter(t => t.created_by === user?.id).length,
    my_to_receive: transfers.filter(t => 
      t.status === 'arrived' && userStoreLocations.includes(t.to_unit_id)
    ).length
  }), [transfers, user?.id, userStoreLocations]);

  // Clear filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      fromUnitId: 'all',
      toUnitId: 'all',
      search: ''
    });
  };

  // Export to Excel
  const handleExport = () => {
    const dataToExport = filteredTransfers.map(t => ({
      'IUT Number': t.transfer_number,
      'From Unit': t.from_unit_name,
      'To Unit': t.to_unit_name,
      'Item': t.item_name,
      'Quantity': t.quantity,
      'Unit': t.unit,
      'Status': t.status,
      'Gate Pass': t.gate_pass_number || '-',
      'Vehicle': t.vehicle_no || '-',
      'Created By': t.created_by_name,
      'Created At': t.created_at ? format(new Date(t.created_at), 'dd-MMM-yyyy HH:mm') : '-',
      'Dispatched At': t.dispatched_at ? format(new Date(t.dispatched_at), 'dd-MMM-yyyy HH:mm') : '-',
      'Received At': t.received_at ? format(new Date(t.received_at), 'dd-MMM-yyyy HH:mm') : '-',
      'Received By': t.received_by_name || '-'
    }));
    
    // Create CSV content
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => 
        headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IUT_Register_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export completed');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      from_unit_id: '',
      to_unit_id: '',
      remarks: ''
    });
    setIssueItems([]);
  };

  // Open edit dialog with transfer data
  const openEditDialog = (transfer) => {
    setSelectedTransfer(transfer);
    setEditData({
      from_unit_id: transfer.from_unit_id,
      to_unit_id: transfer.to_unit_id,
      remarks: transfer.remarks || ''
    });
    // Load items from transfer
    const transferItems = transfer.items || [{
      item_id: transfer.item_id,
      item_name: transfer.item_name,
      quantity: transfer.quantity,
      unit: transfer.unit,
      item_code: transfer.item_code || '',
      item_remarks: ''
    }];
    setEditItems(transferItems.map(item => ({
      item_name: item.item_name,
      quantity: String(item.quantity),
      unit: item.unit || 'NOS',
      item_code: item.item_code || '',
      remarks: item.item_remarks || ''
    })));
    setOpenEdit(true);
  };

  // Add new blank item to issue list
  const addIssueItem = () => {
    setIssueItems([...issueItems, {
      item_name: '',
      quantity: '',
      unit: 'NOS',
      item_code: '',
      remarks: ''
    }]);
  };

  // Remove item from issue list
  const removeIssueItem = (index) => {
    setIssueItems(issueItems.filter((_, i) => i !== index));
  };

  // Update issue item
  const updateIssueItem = (index, field, value) => {
    const updated = [...issueItems];
    updated[index] = { ...updated[index], [field]: value };
    setIssueItems(updated);
  };

  // Add new blank item to edit list
  const addEditItem = () => {
    setEditItems([...editItems, {
      item_name: '',
      quantity: '',
      unit: 'NOS',
      item_code: '',
      remarks: ''
    }]);
  };

  // Remove item from edit list
  const removeEditItem = (index) => {
    if (editItems.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  // Update edit item
  const updateEditItem = (index, field, value) => {
    const updated = [...editItems];
    updated[index] = { ...updated[index], [field]: value };
    setEditItems(updated);
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    if (!editData.from_unit_id) {
      toast.error('Please select source location');
      return;
    }
    if (!editData.to_unit_id) {
      toast.error('Please select destination location');
      return;
    }
    if (editData.from_unit_id === editData.to_unit_id) {
      toast.error('Source and destination cannot be the same');
      return;
    }
    if (editItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    // Validate all items have name and quantity
    for (const item of editItems) {
      if (!item.item_name || !item.item_name.trim()) {
        toast.error('Please enter item name for all items');
        return;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Please enter valid quantity for ${item.item_name}`);
        return;
      }
    }

    try {
      await axios.patch(`${API_URL}/inter-unit-transfers/${selectedTransfer.id}`, {
        from_unit_id: editData.from_unit_id,
        to_unit_id: editData.to_unit_id,
        items: editItems.map(item => ({
          item_name: item.item_name.trim(),
          quantity: parseFloat(item.quantity),
          unit: item.unit || 'NOS',
          item_code: item.item_code || null,
          remarks: item.remarks || null
        })),
        remarks: editData.remarks
      }, { headers: getAuthHeader() });
      
      toast.success('Transfer updated successfully');
      setOpenEdit(false);
      setSelectedTransfer(null);
      setEditItems([]);
      fetchData();
    } catch (error) {
      console.error('Error updating transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update transfer');
    }
  };

  const handleSubmit = async () => {
    if (!formData.from_unit_id) {
      toast.error('Please select source location');
      return;
    }
    if (!formData.to_unit_id) {
      toast.error('Please select destination location');
      return;
    }
    if (formData.from_unit_id === formData.to_unit_id) {
      toast.error('Source and destination cannot be the same');
      return;
    }
    if (issueItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    // Validate all items have name and quantity
    for (const item of issueItems) {
      if (!item.item_name || !item.item_name.trim()) {
        toast.error('Please enter item name for all items');
        return;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Please enter valid quantity for ${item.item_name}`);
        return;
      }
    }

    try {
      const response = await axios.post(`${API_URL}/inter-unit-transfers/issue`, {
        from_unit_id: formData.from_unit_id,
        to_unit_id: formData.to_unit_id,
        items: issueItems.map(item => ({
          item_name: item.item_name.trim(),
          quantity: parseFloat(item.quantity),
          unit: item.unit || 'NOS',
          item_code: item.item_code || null,
          remarks: item.remarks || null
        })),
        remarks: formData.remarks
      }, { headers: getAuthHeader() });
      
      const data = response.data;
      if (data.gate_pass_number) {
        toast.success(`IUT ${data.transfer?.transfer_number} issued with Gate Pass ${data.gate_pass_number}`);
      } else {
        toast.success(data.message || 'Transfer issued successfully');
      }
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error issuing transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to issue transfer');
    }
  };

  const handleReceive = async () => {
    if (!selectedTransfer) return;
    
    // Validate all items
    for (const item of receiveItems) {
      const sentQty = item.quantity || 0;
      const qtyOk = parseFloat(item.qty_ok) || 0;
      const qtyNotOk = parseFloat(item.qty_not_ok) || 0;
      
      if (qtyOk + qtyNotOk !== sentQty) {
        toast.error(`Total (Qty OK + Qty NOT OK) must equal sent quantity (${sentQty}) for ${item.item_name}`);
        return;
      }
    }
    
    try {
      await axios.post(`${API_URL}/inter-unit-transfers/${selectedTransfer.id}/receive`, {
        items: receiveItems.map(item => ({
          item_name: item.item_name,
          qty_ok: parseFloat(item.qty_ok) || 0,
          qty_not_ok: parseFloat(item.qty_not_ok) || 0,
          remarks: item.remarks || null
        }))
      }, { headers: getAuthHeader() });
      toast.success('Transfer received successfully');
      setOpenReceive(false);
      setSelectedTransfer(null);
      setReceiveItems([]);
      fetchData();
    } catch (error) {
      console.error('Error receiving transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to receive transfer');
    }
  };

  // Update receive item
  const updateReceiveItem = (index, field, value) => {
    const updated = [...receiveItems];
    const item = updated[index];
    item[field] = value;
    
    // Auto-calculate the other qty field
    if (field === 'qty_ok') {
      const qtyOk = parseFloat(value) || 0;
      item.qty_not_ok = String(Math.max(0, item.quantity - qtyOk));
    } else if (field === 'qty_not_ok') {
      const qtyNotOk = parseFloat(value) || 0;
      item.qty_ok = String(Math.max(0, item.quantity - qtyNotOk));
    }
    
    setReceiveItems(updated);
  };

  const handleCancel = async (transfer) => {
    try {
      await axios.post(`${API_URL}/inter-unit-transfers/${transfer.id}/cancel`, {}, { headers: getAuthHeader() });
      toast.success('Transfer cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel transfer');
    }
  };

  // Open receive dialog and prepare items
  const openReceiveDialog = (transfer) => {
    setSelectedTransfer(transfer);
    // Initialize receive items from transfer items
    const transferItems = transfer.items || [{
      item_id: transfer.item_id,
      item_name: transfer.item_name,
      quantity: transfer.quantity,
      unit: transfer.unit
    }];
    setReceiveItems(transferItems.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      quantity: item.quantity,
      unit: item.unit,
      qty_ok: String(item.quantity),
      qty_not_ok: '0',
      remarks: ''
    })));
    setOpenReceive(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inter-Unit Transfers</h1>
            <p className="text-sm text-gray-500 mt-1">
              Transfer materials and items between physical locations/units
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Export
            </Button>
            <Button onClick={() => setOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Issue IUT
          </Button>
          </div>
        </div>

        {/* Filters Row */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="IUT number, item, unit..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-8 h-9"
                  />
                </div>
              </div>
              <div className="w-[140px]">
                <Label className="text-xs text-gray-500">From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="w-[140px]">
                <Label className="text-xs text-gray-500">To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="w-[180px]">
                <Label className="text-xs text-gray-500">From Unit</Label>
                <Select value={filters.fromUnitId} onValueChange={(v) => setFilters({ ...filters, fromUnitId: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Units</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[180px]">
                <Label className="text-xs text-gray-500">To Unit</Label>
                <Select value={filters.toUnitId} onValueChange={(v) => setFilters({ ...filters, toUnitId: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Units</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transfers Table with Tabs */}
        <Card>
          <CardHeader className="pb-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex-wrap h-auto gap-1">
                <TabsTrigger value="all" className="gap-1">
                  All <Badge variant="secondary" className="ml-1">{tabCounts.all}</Badge>
                </TabsTrigger>
                <TabsTrigger value="pending" className="gap-1">
                  Pending <Badge variant="secondary" className="ml-1">{tabCounts.pending}</Badge>
                </TabsTrigger>
                <TabsTrigger value="in_transit" className="gap-1">
                  In Transit <Badge variant="secondary" className="ml-1">{tabCounts.in_transit}</Badge>
                </TabsTrigger>
                <TabsTrigger value="arrived" className="gap-1">
                  Arrived <Badge variant="secondary" className="ml-1">{tabCounts.arrived}</Badge>
                </TabsTrigger>
                <TabsTrigger value="received" className="gap-1">
                  Received <Badge variant="secondary" className="ml-1">{tabCounts.received}</Badge>
                </TabsTrigger>
                <TabsTrigger value="my_issued" className="gap-1 border-l ml-2 pl-3">
                  My Issued <Badge variant="secondary" className="ml-1">{tabCounts.my_issued}</Badge>
                </TabsTrigger>
                {userStoreLocations.length > 0 && (
                  <TabsTrigger value="my_to_receive" className="gap-1">
                    Pending Receiving <Badge variant="secondary" className="ml-1">{tabCounts.my_to_receive}</Badge>
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : filteredTransfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No transfers found.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transfer #</TableHead>
                    <TableHead>From → To</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Gate Pass</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.map((transfer) => (
                    <TableRow key={transfer.id}>
                      <TableCell>
                        <span className="font-mono text-sm">{transfer.transfer_number}</span>
                        {transfer.work_order_number && (
                          <div className="text-xs text-gray-500">WO: {transfer.work_order_number}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{transfer.from_unit_code}</Badge>
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                          <Badge variant="outline">{transfer.to_unit_code}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{transfer.item_name}</div>
                        {(transfer.item_count || (transfer.items && transfer.items.length > 1)) && (
                          <div className="text-xs text-gray-500">
                            +{(transfer.item_count || transfer.items?.length || 1) - 1} more items
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{transfer.quantity}</span>
                        <span className="text-gray-500 ml-1">{(transfer.item_count || 1) > 1 ? 'total' : transfer.unit}</span>
                      </TableCell>
                      <TableCell>
                        {transfer.gate_pass_number ? (
                          <div className="text-xs">
                            <div className="text-blue-600 font-medium">{transfer.gate_pass_number}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[transfer.status]} gap-1`}>
                          {statusIcons[transfer.status]}
                          {transfer.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {transfer.created_at ? format(new Date(transfer.created_at), 'dd MMM yyyy') : '-'}
                        <div className="text-xs text-gray-400">{transfer.created_by_name}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {/* View/Print button - always available */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            onClick={() => {
                              setSelectedTransfer(transfer);
                              setOpenView(true);
                            }}
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </Button>
                          
                          {transfer.status === 'pending' && (
                            <>
                              <span className="text-xs text-yellow-600 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Awaiting Gate Out
                              </span>
                              {canEdit(transfer) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  onClick={() => openEditDialog(transfer)}
                                >
                                  <Edit className="h-3 w-3" />
                                  Edit
                                </Button>
                              )}
                              {canCancel(transfer) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600"
                                  onClick={() => handleCancel(transfer)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </>
                          )}
                          {transfer.status === 'arrived' && canReceive(transfer) && (
                            <Button
                              variant="default"
                              size="sm"
                              className="gap-1"
                              onClick={() => openReceiveDialog(transfer)}
                            >
                              <Download className="h-3 w-3" />
                              Receive
                            </Button>
                          )}
                          {transfer.status === 'in_transit' && (
                            <Badge variant="outline" className="text-blue-600">
                              <Truck className="h-3 w-3 mr-1" />
                              Awaiting Gate IN
                            </Badge>
                          )}
                          {transfer.status === 'received' && (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Issue IUT Dialog */}
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Issue Inter-Unit Transfer</DialogTitle>
              <DialogDescription>
                Issue materials to another location. Gate Pass will be auto-created.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Location <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.from_unit_id}
                    onValueChange={(value) => setFormData({ ...formData, from_unit_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.code} - {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Location <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.to_unit_id}
                    onValueChange={(value) => setFormData({ ...formData, to_unit_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.code} - {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items <span className="text-red-500">*</span></Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addIssueItem}
                    className="gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Items Table */}
              {issueItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2 font-medium">Item Name <span className="text-red-500">*</span></th>
                        <th className="text-center p-2 font-medium w-24">Qty <span className="text-red-500">*</span></th>
                        <th className="text-center p-2 font-medium w-20">Unit</th>
                        <th className="text-center p-2 font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {issueItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2">
                            <Input
                              type="text"
                              value={item.item_name}
                              onChange={(e) => updateIssueItem(index, 'item_name', e.target.value)}
                              className="h-8"
                              placeholder="Enter item name"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={item.quantity}
                              onChange={(e) => updateIssueItem(index, 'quantity', e.target.value)}
                              className="h-8 text-center"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={item.unit}
                              onValueChange={(value) => updateIssueItem(index, 'unit', value)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="NOS">NOS</SelectItem>
                                <SelectItem value="KG">KG</SelectItem>
                                <SelectItem value="MTR">MTR</SelectItem>
                                <SelectItem value="LTR">LTR</SelectItem>
                                <SelectItem value="SET">SET</SelectItem>
                                <SelectItem value="BOX">BOX</SelectItem>
                                <SelectItem value="PCS">PCS</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              onClick={() => removeIssueItem(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {issueItems.length === 0 && (
                <div className="text-center py-4 text-gray-400 border rounded-lg border-dashed">
                  Click "Add Item" to add items to this transfer
                </div>
              )}

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea
                  placeholder="Optional notes for this transfer"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={issueItems.length === 0}>
                Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Transfer Dialog */}
        <Dialog open={openReceive} onOpenChange={(open) => {
          setOpenReceive(open);
          if (!open) {
            setReceiveItems([]);
            setSelectedTransfer(null);
          }
        }}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Receive Transfer</DialogTitle>
              <DialogDescription>
                Inspect goods and confirm receipt for {selectedTransfer?.transfer_number}
              </DialogDescription>
            </DialogHeader>
            
            {selectedTransfer && (
              <div className="py-4 space-y-4">
                {/* Transfer Info */}
                <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
                  <div>
                    <span className="text-gray-500">From:</span>
                    <span className="ml-2 font-medium">{selectedTransfer.from_unit_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">To:</span>
                    <span className="ml-2 font-medium">{selectedTransfer.to_unit_name}</span>
                  </div>
                  {selectedTransfer.vehicle_no && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Vehicle:</span>
                      <span className="ml-2 font-medium">{selectedTransfer.vehicle_no}</span>
                    </div>
                  )}
                </div>

                {/* Items Quality Check Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2 font-medium">Item</th>
                        <th className="text-center p-2 font-medium w-20">Sent</th>
                        <th className="text-center p-2 font-medium w-24">Qty OK</th>
                        <th className="text-center p-2 font-medium w-24">Qty NOT OK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiveItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{item.item_name}</div>
                            <div className="text-xs text-gray-500">{item.unit}</div>
                          </td>
                          <td className="p-2 text-center font-medium">
                            {item.quantity}
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="0"
                              max={item.quantity}
                              value={item.qty_ok}
                              onChange={(e) => updateReceiveItem(index, 'qty_ok', e.target.value)}
                              className="h-8 text-center"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="0"
                              max={item.quantity}
                              value={item.qty_not_ok}
                              onChange={(e) => updateReceiveItem(index, 'qty_not_ok', e.target.value)}
                              className="h-8 text-center"
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Show remarks input if any item has qty_not_ok > 0 */}
                {receiveItems.some(item => parseFloat(item.qty_not_ok) > 0) && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-red-600">
                      Reason for Quality Issues
                    </label>
                    {receiveItems.filter(item => parseFloat(item.qty_not_ok) > 0).map((item, idx) => (
                      <div key={idx} className="text-xs text-gray-600 mb-1">
                        {item.item_name}: {item.qty_not_ok} {item.unit} NOT OK
                      </div>
                    ))}
                    <Textarea
                      value={receiveItems[0]?.remarks || ''}
                      onChange={(e) => {
                        const updated = receiveItems.map(item => ({...item, remarks: e.target.value}));
                        setReceiveItems(updated);
                      }}
                      placeholder="Describe the quality issue (e.g., damaged, defective, short quantity)"
                      rows={2}
                    />
                  </div>
                )}

                {/* Summary */}
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between font-medium">
                    <span>Total Items:</span>
                    <span>{receiveItems.length}</span>
                  </div>
                  <div className="flex justify-between text-green-600 mt-1">
                    <span>All OK:</span>
                    <span>{receiveItems.filter(i => parseFloat(i.qty_not_ok) === 0).length} items</span>
                  </div>
                  {receiveItems.some(i => parseFloat(i.qty_not_ok) > 0) && (
                    <div className="flex justify-between text-red-600">
                      <span>With Issues:</span>
                      <span>{receiveItems.filter(i => parseFloat(i.qty_not_ok) > 0).length} items</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setOpenReceive(false);
                setReceiveItems([]);
                setSelectedTransfer(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleReceive} 
                className="gap-2"
                disabled={receiveItems.length === 0}
              >
                <CheckCircle className="h-4 w-4" />
                Confirm Receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit IUT Dialog */}
        <Dialog open={openEdit} onOpenChange={(open) => {
          setOpenEdit(open);
          if (!open) {
            setSelectedTransfer(null);
            setEditItems([]);
            setEditSearchItem('');
            setEditFilteredItems([]);
          }
        }}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Inter-Unit Transfer</DialogTitle>
              <DialogDescription>
                Modify transfer details for {selectedTransfer?.transfer_number}. Only pending transfers can be edited.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Location <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={locations.map(loc => ({ value: loc.id, label: `${loc.code} - ${loc.name}` })).sort((a, b) => a.label.localeCompare(b.label))}
                    value={editData.from_unit_id}
                    onValueChange={(value) => setEditData({ ...editData, from_unit_id: value })}
                    placeholder="Select source..."
                    searchPlaceholder="Search location..."
                    emptyText="No locations found"
                  />
                </div>
                <div className="space-y-2">
                  <Label>To Location <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={locations.map(loc => ({ value: loc.id, label: `${loc.code} - ${loc.name}` })).sort((a, b) => a.label.localeCompare(b.label))}
                    value={editData.to_unit_id}
                    onValueChange={(value) => setEditData({ ...editData, to_unit_id: value })}
                    placeholder="Select destination..."
                    searchPlaceholder="Search location..."
                    emptyText="No locations found"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items <span className="text-red-500">*</span></Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addEditItem}
                    className="gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Items Table */}
              {editItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2 font-medium">Item Name <span className="text-red-500">*</span></th>
                        <th className="text-center p-2 font-medium w-24">Qty <span className="text-red-500">*</span></th>
                        <th className="text-center p-2 font-medium w-20">Unit</th>
                        <th className="text-center p-2 font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2">
                            <SearchableSelect
                              options={items.map(i => ({ 
                                value: i.name, 
                                label: `${i.item_code || ''} ${i.name}`.trim(),
                                code: i.item_code || '',
                                category: i.category || ''
                              })).sort((a, b) => a.label.localeCompare(b.label))}
                              value={item.item_name}
                              onValueChange={(value) => updateEditItem(index, 'item_name', value)}
                              placeholder="Select item..."
                              searchPlaceholder="Search items..."
                              emptyText="No items found"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={item.quantity}
                              onChange={(e) => updateEditItem(index, 'quantity', e.target.value)}
                              className="h-8 text-center"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={item.unit}
                              onValueChange={(value) => updateEditItem(index, 'unit', value)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="NOS">NOS</SelectItem>
                                <SelectItem value="KG">KG</SelectItem>
                                <SelectItem value="MTR">MTR</SelectItem>
                                <SelectItem value="LTR">LTR</SelectItem>
                                <SelectItem value="SET">SET</SelectItem>
                                <SelectItem value="BOX">BOX</SelectItem>
                                <SelectItem value="PCS">PCS</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              onClick={() => removeEditItem(index)}
                              disabled={editItems.length <= 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea
                  placeholder="Optional notes for this transfer"
                  value={editData.remarks}
                  onChange={(e) => setEditData({ ...editData, remarks: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setOpenEdit(false);
                setSelectedTransfer(null);
                setEditItems([]);
              }}>
                Cancel
              </Button>
              <Button onClick={handleEditSubmit} disabled={editItems.length === 0}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View/Print IUT Dialog */}
        <Dialog open={openView} onOpenChange={(open) => {
          setOpenView(open);
          if (!open) {
            setSelectedTransfer(null);
            setPrintCopyType('ORIGINAL');
          }
        }}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                View Inter-Unit Transfer
              </DialogTitle>
              <DialogDescription>
                {selectedTransfer?.transfer_number} - Print or view transfer details
              </DialogDescription>
            </DialogHeader>
            
            {/* Print Copy Type Selection */}
            <div className="flex items-center gap-4 py-2 border-b">
              <Label>Copy Type:</Label>
              <div className="flex gap-2">
                {['ORIGINAL', 'DUPLICATE', 'TRIPLICATE'].map((type) => (
                  <Button
                    key={type}
                    variant={printCopyType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPrintCopyType(type)}
                  >
                    {type}
                  </Button>
                ))}
              </div>
              <div className="flex-1" />
              <Button 
                onClick={handlePrint} 
                className="gap-2"
              >
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>

            {/* Print Preview */}
            <div className="border rounded-lg overflow-auto bg-gray-50 p-4" style={{ maxHeight: '60vh' }}>
              <div className="bg-white shadow-lg">
                <IUTPrint 
                  ref={printRef} 
                  transfer={selectedTransfer} 
                  copyType={printCopyType}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setOpenView(false);
                setSelectedTransfer(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
